/** A category used to organize enterprise media items. Categories are organized in a two-level hierarchy: a root account category (created during onboarding) and categories nested within it. */
export interface EnterpriseCategory {
  /**
   * Category ID.
   * @maxLength 100
   * @readonly
   */
  id?: string;
  /**
   * Display name shown for the category.
   * @maxLength 100
   * @minLength 2
   */
  displayName?: string | null;
  /**
   * ID of the parent category.
   *
   * Default: Account category ID.
   * @maxLength 100
   */
  parentCategoryId?: string | null;
  /** Display order among sibling categories under the same parent. Lower values appear first. Supports decimals to insert between existing positions without renumbering. */
  sortOrder?: number | null;
  /** Publication status of the category. Controls whether the category is visible to child accounts. */
  publishStatus?: PublishStatusWithLiterals;
  /**
   * Date and time the category was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the category was updated.
   * @readonly
   */
  updatedDate?: Date | null;
}

export enum PublishStatus {
  /** Unknown publish status. */
  UNDEFINED = 'UNDEFINED',
  /** Not published. Visible only in the account dashboard. */
  UNPUBLISHED = 'UNPUBLISHED',
  /** Published. Visible in child accounts' media managers. */
  PUBLISHED = 'PUBLISHED',
}

/** @enumType */
export type PublishStatusWithLiterals =
  | PublishStatus
  | 'UNDEFINED'
  | 'UNPUBLISHED'
  | 'PUBLISHED';

export enum MediaType {
  /** Mixed media types. Used for categories that contain multiple types of media. Individual items should use specific media types (IMAGE, VIDEO, etc.) rather than MIXED. */
  MIXED = 'MIXED',
  /** Image file. */
  IMAGE = 'IMAGE',
  /** Video file. */
  VIDEO = 'VIDEO',
  /** Audio file. */
  AUDIO = 'AUDIO',
  /** Document file. */
  DOCUMENT = 'DOCUMENT',
  /** Vector image file. */
  VECTOR = 'VECTOR',
  /** Archive file. Supported formats: ZIP, RAR, TAR, TAR.GZ, GZ, GZIP, JAR, 7Z, FGZ, and WEBARCHIVE. Maximum file size: 4000MB. */
  ARCHIVE = 'ARCHIVE',
  /** 3D model file. */
  MODEL3D = 'MODEL3D',
}

/** @enumType */
export type MediaTypeWithLiterals =
  | MediaType
  | 'MIXED'
  | 'IMAGE'
  | 'VIDEO'
  | 'AUDIO'
  | 'DOCUMENT'
  | 'VECTOR'
  | 'ARCHIVE'
  | 'MODEL3D';

export interface CreateCategoryRequest {
  /** Category to create. */
  category: EnterpriseCategory;
}

export interface CreateCategoryResponse {
  /** Created category. */
  category?: EnterpriseCategory;
}

export interface DeleteCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId: string;
}

export interface DeleteCategoryResponse {}

export interface UpdateCategoryRequest {
  /** Category to update. */
  category: EnterpriseCategory;
}

export interface UpdateCategoryResponse {
  /** Updated category. */
  category?: EnterpriseCategory;
}

export interface GetCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId: string;
  /** Number of subcategory levels to include in the response. For example, `levels=1` returns the category with its immediate children, `levels=2` includes grandchildren as well. When not specified, returns only the requested category without subcategories. */
  levels?: number | null;
  /** Publish status to filter categories by. Returns both `PUBLISHED` and `UNPUBLISHED` categories by default. When specified in addition to `levels`, this filter applies to all categories in the response, not just the root. */
  publishStatus?: PublishStatusWithLiterals;
}

export interface GetCategoryResponse {
  /** Category details including subcategories if requested. */
  category?: EnterpriseCategoryTree;
}

/** A category and its nested categories, as returned by [Get Category](#getcategory). */
export interface EnterpriseCategoryTree {
  /** Category information. */
  category?: EnterpriseCategory;
  /**
   * Nested categories under this category. Only populated when retrieving an account-level category; nested categories don't have further sub-categories.
   * @maxSize 100
   */
  subCategories?: EnterpriseCategoryTree[];
}

export interface EnterpriseOnboardingRequest {
  /**
   * Account ID of the organization. Used as the organization category ID.
   * @format GUID
   */
  accountId: string;
  /**
   * Account name of the organization. Used as the organization category name.
   * @maxLength 50
   */
  accountName?: string;
}

export interface EnterpriseOnboardingResponse {
  /** Created enterprise category. */
  category?: EnterpriseCategory;
}

export interface LinkItemsToCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId?: string;
  /**
   * IDs of items to link to the category.
   * @maxLength 100
   * @maxSize 50
   */
  itemIds?: string[];
}

export interface LinkItemsToCategoryResponse {}

export interface UnlinkItemsFromCategoryRequest {
  /**
   * Category ID.
   * @maxLength 100
   */
  categoryId?: string;
  /**
   * IDs of items to unlink from the category.
   * @maxLength 100
   * @maxSize 50
   */
  itemIds?: string[];
}

export interface UnlinkItemsFromCategoryResponse {}

export interface GetMediaManagerCategoriesRequest {}

export interface GetMediaManagerCategoriesResponse {
  /** Category tree for the media manager. */
  category?: EnterpriseCategoryTree;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
